import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { showErrorAlert, showSuccessAlert } from 'actions';
import { upsertCohort, upsertCohortFailure, upsertCohortSuccess } from 'actions/cohorts';
import {
  fetchIndexHealthSample,
  fetchIndexHealthSampleFailure,
  fetchIndexHealthSampleRequest,
  fetchIndexHealthSampleSuccess,
  saveSOWUsersAndImport,
  saveSOWUsersAndImportFailure,
  saveSOWUsersAndImportRequest,
  saveSOWUsersAndImportSuccess,
} from 'actions/customers';
import { saveUsers, saveUsersFailure, saveUsersSuccess } from 'actions/users';
import { apiClient as LitLingoClient } from 'client';
import { push } from 'connected-react-router';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import { call, put, select, take, takeLatest } from 'redux-saga/effects';
import { getCustomerDomain, getUserCustomer } from 'selectors/auth';
import { getCohort } from 'selectors/cohorts';
import { getNavParamsByResource } from 'selectors/nav';
import { API, Cohort, ErrorObject, SagaReturn } from 'types';
import { reverse } from 'utils/urls';

function* fetchIndexHealthSampleSaga(): SagaReturn {
  yield put(fetchIndexHealthSampleRequest());

  const resourceParams = (yield select(
    getNavParamsByResource(resourceQueryParamName.envelopes)
  )) as ReturnType<ReturnType<typeof getNavParamsByResource>>;

  const response = (yield call([LitLingoClient.resources.customers.extras, 'indexHealthSample'], {
    params: { ...resourceParams },
  })) as API.Response<API.Customers.IndexHealthSample>;
  if (response.error != null) {
    yield put(fetchIndexHealthSampleFailure(response.error));
  } else {
    yield put(fetchIndexHealthSampleSuccess(response.data));
  }
}

function* saveSOWUsersAndImportSaga(action: ReturnType<typeof saveSOWUsersAndImport>): SagaReturn {
  const { payload } = action;
  const customer = (yield select(getUserCustomer)) as ReturnType<typeof getUserCustomer>;
  const cohort = (yield select(getCohort)) as ReturnType<typeof getCohort>;

  yield put(saveSOWUsersAndImportRequest());

  yield put(
    upsertCohort({
      cohort: { ...cohort, ...payload.sow, config: { ...cohort?.config, ...payload.sow.config } },
    })
  );
  const { payload: cohortPayload } = (yield take([
    upsertCohortSuccess.toString(),
    upsertCohortFailure.toString(),
  ])) as ReturnType<ActionCreatorWithPayload<ErrorObject | Cohort>>;

  if ('message' in cohortPayload) {
    yield put(saveSOWUsersAndImportFailure(cohortPayload));
    return;
  }

  if (payload.users.length > 0) {
    yield put(
      saveUsers({
        cohort_uuid: cohortPayload.uuid,
        users: payload.users,
        fromPermissionsPage: true,
      })
    );
    const returnedAction = (yield take([
      saveUsersSuccess.toString(),
      saveUsersFailure.toString(),
    ])) as ReturnType<ActionCreatorWithPayload<ErrorObject>>;

    if (returnedAction.type === saveUsersFailure.toString()) {
      yield put(saveSOWUsersAndImportFailure(returnedAction.payload));
      return;
    }
  }

  if (payload.doO365Sweep) {
    const response = (yield call([LitLingoClient.resources.tasks.extras, 'sweepO365'], {
      data: { customer_uuid: customer?.uuid || '', cohort_uuid: cohortPayload.uuid },
    })) as API.Response<API.Customers.IndexHealthSample>;

    if (response.error != null) {
      yield put(saveSOWUsersAndImportFailure(response.error));
      yield put(showErrorAlert('Failed to start O365 Import.'));
      return;
    }
  }

  if (payload.doTeamsweep) {
    const response = (yield call([LitLingoClient.resources.tasks.extras, 'sweepTeams'], {
      data: { customer_uuid: customer?.uuid || '', cohort_uuid: cohortPayload.uuid },
    })) as API.Response<API.Customers.IndexHealthSample>;

    if (response.error != null) {
      yield put(saveSOWUsersAndImportFailure(response.error));
      yield put(showErrorAlert('Failed to start Teams Import.'));
      return;
    }
  }

  yield put(saveSOWUsersAndImportSuccess());
  yield put(showSuccessAlert('Import data kickoff successfully queued up.'));

  const customerDomain = (yield select(getCustomerDomain)) as string;
  const path = reverse({
    routeName: 'customer-cohort',
    customerDomain,
    routeParams: { cohortId: cohortPayload.uuid },
  });
  yield put(push(path));
}

function* customersSaga(): SagaReturn {
  yield takeLatest(fetchIndexHealthSample, fetchIndexHealthSampleSaga);
  yield takeLatest(saveSOWUsersAndImport, saveSOWUsersAndImportSaga);
}

export default customersSaga;
